import styled from '@emotion/styled';
import {
  SpeakingSessionRoomStructureAssignedExaminerDto,
  SpeakingSlotDto,
  SpeakingSlotType,
} from 'api/planning';
import { SpeakingSlotAction } from './SpeakingSlotAction';
import { useAllocationGrid } from './useAllocationGrid';
import { SpeakingSlot } from './SpeakingSlot';
import classNames from 'classnames';
import { RoomStructureExaminerAllocation } from '../examiners/RoomStructureExaminerAllocation';
import { RoomClashPeriodDto } from './utils/calculateClashedPeriods';
import { NUMBER_OF_SLOTS, NUMBER_OF_SLOTS_PER_HOUR } from './speakingSlotTime';

type Props = {
  sessionRoomId?: number;
  sessionRoomStructureId?: number;
  speakingSessionDate?: string;
  localSpeakingSlotMinutesOffset: number;
  slots: SpeakingSlotDto[];
  occupiedSlots: SpeakingSlotDto[];
  roomSwaSlots: SpeakingSlotDto[];
  clashedPeriods: RoomClashPeriodDto[];
  editing: boolean;
  selectedSlotAction: SpeakingSlotAction;
  speakingSlotType: SpeakingSlotType;
  assignedExaminer?: SpeakingSessionRoomStructureAssignedExaminerDto;
  onChangeSlots: (value: SpeakingSlotDto[]) => void;
  roomCapacity: number;
  plannedCapacity: number;
  minCapacity: number;
  onCapacityChange: (capacity: number) => void;
};

export const AllocationGrid = ({
  sessionRoomId,
  sessionRoomStructureId,
  speakingSessionDate,
  localSpeakingSlotMinutesOffset,
  editing,
  slots,
  occupiedSlots,
  roomSwaSlots,
  clashedPeriods,
  selectedSlotAction,
  speakingSlotType,
  assignedExaminer,
  roomCapacity,
  plannedCapacity,
  minCapacity,
  onCapacityChange,

  onChangeSlots,
}: Props) => {
  const {
    grid,
    selecting,
    onMouseDown,
    onMouseEnterSlot,
    onMouseLeave,
    structureStartTime,
    structureEndTime,
  } = useAllocationGrid({
    editing,
    localSpeakingSlotMinutesOffset,
    slots,
    occupiedSlots,
    roomSwaSlots,
    clashedPeriods,
    selectedSlotAction,
    speakingSlotType,
    plannedCapacity,
    onChangeSlots,
  });

  const cells = grid.map((x) => (
    <SpeakingSlot
      cell={x}
      key={x.startTime}
      onMouseEnter={onMouseEnterSlot}
      speakingSlotType={speakingSlotType}
    />
  ));

  return (
    <S.Grid numberOfSlots={NUMBER_OF_SLOTS} numberOfSlotsPerHour={NUMBER_OF_SLOTS_PER_HOUR}>
      <RoomStructureExaminerAllocation
        editing={editing}
        assignedExaminer={assignedExaminer}
        sessionRoomId={sessionRoomId}
        sessionRoomStructureId={sessionRoomStructureId}
        structureStartTime={structureStartTime}
        structureEndTime={structureEndTime}
        speakingSlotType={speakingSlotType}
        speakingSessionDate={speakingSessionDate}
        clashedPeriods={clashedPeriods}
        roomCapacity={roomCapacity}
        plannedCapacity={plannedCapacity}
        minCapacity={minCapacity}
        onCapacityChange={onCapacityChange}
      />
      <S.Slots
        onMouseDown={onMouseDown}
        onMouseLeave={onMouseLeave}
        className={classNames({ selecting })}
        data-testid="allocation-grid-slots"
      >
        {cells}
      </S.Slots>
    </S.Grid>
  );
};

const S = {
  Grid: styled.div<{ numberOfSlots: number; numberOfSlotsPerHour: number }>`
    display: grid;
    grid-template-rows: 25px 42px;
    grid-template-columns: repeat(
      ${(props) => props.numberOfSlots},
      minmax(calc(90px / ${(props) => props.numberOfSlotsPerHour}), 1fr)
    );
  `,
  Slots: styled.div`
    user-select: none;
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
  `,
};
